import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { getOrgActivity, type FeedRun, type FeedTx } from './api';

const EVM_EXPLORERS: Record<string, string> = {
  ethereum: 'https://etherscan.io/tx/',
  polygon: 'https://polygonscan.com/tx/',
  bsc: 'https://bscscan.com/tx/',
  arbitrum: 'https://arbiscan.io/tx/',
  optimism: 'https://optimistic.etherscan.io/tx/',
  base: 'https://basescan.org/tx/',
  sepolia: 'https://sepolia.etherscan.io/tx/',
};

export function ActivityPage() {
  const { org } = useCurrentOrg();
  const orgId = org?.id ?? '';

  const feed = useQuery({
    queryKey: ['org-activity', orgId, 30],
    queryFn: () => getOrgActivity(orgId, 30),
    enabled: !!orgId,
    refetchInterval: 10_000,
  });

  const activeRuns = feed.data?.active_runs ?? 0;
  const runs = feed.data?.latest_runs ?? [];
  const txs = feed.data?.latest_tx ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tightish text-ink-primary">Activity</h1>
        <p className="mt-1 text-sm text-ink-secondary">
          Cross-agent stream for the whole company. Refreshes every 10 seconds.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Kpi label="Active runs" value={String(activeRuns)} hint="pending or running" pulse={activeRuns > 0} />
        <Kpi label="Runs (recent)" value={String(runs.length)} hint="latest 30 across all agents" />
        <Kpi label="Transactions (recent)" value={String(txs.length)} hint="latest 30 across all agents" />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Live runs"
            description="Newest first. Click any row to open the trace."
          />
          <CardBody className="p-0">
            {runs.length === 0 ? (
              <p className="px-4 py-8 text-sm text-ink-tertiary">No runs yet.</p>
            ) : (
              <ul className="divide-y divide-line-soft">
                {runs.map((r) => (
                  <RunRow key={r.id} r={r} />
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Live transactions"
            description="Broadcasts and confirmations from every agent."
          />
          <CardBody className="p-0">
            {txs.length === 0 ? (
              <p className="px-4 py-8 text-sm text-ink-tertiary">No transactions yet.</p>
            ) : (
              <ul className="divide-y divide-line-soft">
                {txs.map((t) => (
                  <TxRow key={t.id} t={t} />
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  pulse = false,
}: {
  label: string;
  value: string;
  hint?: string;
  pulse?: boolean;
}) {
  return (
    <div className="rounded-xl border border-line-soft bg-surface-white px-4 py-3">
      <div className="flex items-center gap-2">
        {pulse && <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />}
        <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
          {label}
        </p>
      </div>
      <p className="mt-1 text-2xl font-semibold tracking-tightish text-ink-primary">{value}</p>
      {hint && <p className="font-mono text-[11px] text-ink-tertiary">{hint}</p>}
    </div>
  );
}

function RunRow({ r }: { r: FeedRun }) {
  const isLive = r.status === 'pending' || r.status === 'running';
  return (
    <li>
      <Link
        to={`/agents/${r.agent_id}/runs/${r.id}`}
        className="block px-4 py-3 hover:bg-surface-soft/40"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase text-ink-tertiary">
              <span className="rounded border border-line-soft bg-surface-soft px-1.5 py-0.5">
                {r.agent_template}
              </span>
              <span className="truncate">{r.agent_name}</span>
            </div>
            <p className="mt-1 truncate text-sm text-ink-primary">
              {truncate(r.summary || r.objective, 90)}
            </p>
            <p className="mt-1 font-mono text-[10px] text-ink-tertiary">
              {timeAgo(r.created_at)} · {r.tokens_input}/{r.tokens_output} tok
              {r.outcome && <> · {r.outcome}</>}
            </p>
          </div>
          <StatusPill status={r.status} live={isLive} />
        </div>
      </Link>
    </li>
  );
}

function TxRow({ t }: { t: FeedTx }) {
  const explorer = EVM_EXPLORERS[t.chain];
  const assetLabel = (t.asset || 'native').toUpperCase();
  const amount = t.token_symbol
    ? `${humanFromBaseUnits(t.token_amount, 6)} ${t.token_symbol}`
    : `${humanFromBaseUnits(t.value_wei, 18)} ${assetLabel}`;
  return (
    <li className="px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase text-ink-tertiary">
            <span className="rounded border border-line-soft bg-surface-soft px-1.5 py-0.5">
              {t.agent_template}
            </span>
            <span className="truncate">{t.agent_name}</span>
          </div>
          <p className="mt-1 truncate text-sm text-ink-primary">
            {amount} → {shortenAddress(t.recipient)}
          </p>
          <p className="mt-1 flex items-center gap-2 font-mono text-[10px] text-ink-tertiary">
            <span>{t.chain}</span>
            <span>·</span>
            <span>{shortenAddress(t.tx_hash, 10)}</span>
            {explorer && (
              <a
                href={`${explorer}${t.tx_hash}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-bit2me hover:underline"
              >
                explorer ↗
              </a>
            )}
            <span>·</span>
            <span>{timeAgo(t.broadcast_at)}</span>
          </p>
        </div>
        <TxStatusPill status={t.status} />
      </div>
    </li>
  );
}

function StatusPill({ status, live }: { status: string; live: boolean }) {
  const tone =
    status === 'done'
      ? 'border-ok/30 bg-ok/10 text-ok'
      : status === 'failed'
      ? 'border-danger/30 bg-danger/10 text-danger'
      : status === 'running'
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : 'border-line bg-surface-soft text-ink-tertiary';
  return (
    <span
      className={`flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase ${tone}`}
    >
      {live && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />}
      {status}
    </span>
  );
}

function TxStatusPill({ status }: { status: string }) {
  const tone =
    status === 'confirmed'
      ? 'border-ok/30 bg-ok/10 text-ok'
      : status === 'reverted' || status === 'failed'
      ? 'border-danger/30 bg-danger/10 text-danger'
      : status === 'timeout'
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : 'border-bit2me/30 bg-bit2me/10 text-bit2me';
  return (
    <span
      className={`shrink-0 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase ${tone}`}
    >
      {status}
    </span>
  );
}

function shortenAddress(a: string | undefined | null, head = 6): string {
  a = a ?? '';
  if (!a) return '';
  if (a.length <= head + 4 + 1) return a;
  return `${a.slice(0, head)}…${a.slice(-4)}`;
}

function truncate(s: string | undefined | null, n: number): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function humanFromBaseUnits(raw: string | undefined, decimals: number): string {
  if (!raw) return '0';
  try {
    const n = BigInt(raw);
    if (n === 0n) return '0';
    if (decimals === 0) return n.toString();
    const div = 10n ** BigInt(decimals);
    const whole = n / div;
    const frac = (n % div).toString().padStart(decimals, '0').replace(/0+$/, '');
    return frac ? `${whole}.${frac}` : whole.toString();
  } catch {
    return raw;
  }
}
